import { SimulationScore } from '@/components/Simulations/SimulationScore';
import { SimulationScoreProcessingView } from '@/components/Simulations/SimulationScoreProcessingView';
import { useSimulationScoreViewFeature } from '@/features/simulations/simulation-score-view/useSimulationScoreViewFeature';
import React from 'react';

/**
 * Main view component for displaying the score after a simulation roleplay is completed.
 * It conditionally renders either the score processing view (if scores are still loading)
 * or the final score view once the scores have been processed.
 */
export const SimulationScoreView: React.FC = () => {
  // ===========================================
  // Feature Hook
  // ===========================================
  const {
    activeScore,
    activeSimulation,
    certificationId,
    certificationTitle,
    createdDate,
    handleRetakeRolePlayClick,
    isLoading,
    navigateToDashboard,
    nextSimulation,
    scorecardsLoading,
    scoreProcessingProgress,
    simulationId,
    simulationTitle,
  } = useSimulationScoreViewFeature();

  // ===========================================
  // Render
  // ===========================================
  return scorecardsLoading ? (
    // If scorecards are still loading, show the processing component
    <SimulationScoreProcessingView
      certificationTitle={certificationTitle}
      isLoading={isLoading}
      onGoToDashboard={navigateToDashboard}
      onRetakeRoleplay={handleRetakeRolePlayClick}
      scoreProcessingProgress={scoreProcessingProgress}
      simulationTitle={simulationTitle}
    />
  ) : (
    // Once loading is complete, show the score view
    <SimulationScore
      certificationId={certificationId}
      certificationTitle={certificationTitle}
      complianceFlagText={activeScore?.complianceReminders?.title}
      createdDate={createdDate}
      isLoading={scorecardsLoading}
      nextSimulation={nextSimulation}
      onRetakeRolePlayClick={handleRetakeRolePlayClick}
      score={activeScore}
      scoreIsProcessed={!scorecardsLoading}
      scoreProcessingProgress={scoreProcessingProgress}
      simulation={activeSimulation}
      simulationId={simulationId}
      title={simulationTitle}
    />
  );
};

SimulationScoreView.displayName = 'SimulationScoreView';

export default SimulationScoreView;
